<template>
  <div>
    <v-card outlined>
      <!-- Result Summary -->
      <v-card-subtitle class="d-flex align-center">
        <v-icon left small>mdi-calculator</v-icon>
        <strong>Ergebniswert:</strong>
        <span class="ml-1"
          >{{ resultDisplay.value }}{{ resultDisplay.unit }}</span
        >
      </v-card-subtitle>

      <v-divider></v-divider>

      <!-- Data Variables Section -->
      <v-card-text>
        <div class="pb-3">Dafür wurden folgende Daten herangezogen:</div>
        <div
          v-for="(dataVariable, index) in resultDisplay.dataVariables"
          :key="index"
          class="mb-4"
        >
          <v-card outlined class="">
            <!-- Variable Header -->
            <v-card-title class="py-3 subtitle-2">
              <v-icon left small>mdi-variable</v-icon>
              {{ dataVariable.variable.text }}
            </v-card-title>

            <v-divider></v-divider>

            <!-- Variable Details -->
            <v-card-text class="px-0 py-0">
              <v-list-item dense>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">
                    {{ dataVariable.sourceType.text }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption grey--text">
                    {{ dataVariable.sourceType.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action-text>
                  <v-chip small label class="font-weight-medium">
                    <v-icon small left>mdi-sigma</v-icon>
                    {{ dataVariable.value }}
                    <!-- {{ dataVariable.variable.unit }} -->
                  </v-chip>
                </v-list-item-action-text>
              </v-list-item>
            </v-card-text>

            <!-- Additional Data Section -->
            <div v-if="dataVariable.additionalData !== null">
              <v-divider></v-divider>

              <!-- Toggle Button -->
              <v-card-actions
                class="py-2 px-4"
                @click="toggleDetails(dataVariable)"
                style="cursor: pointer"
              >
                <v-icon small left>mdi-file-document-multiple</v-icon>
                <span v-if="dataVariable.additionalData.length">
                  {{ dataVariable.additionalData.length }}
                  {{
                    dataVariable.additionalData.length === 1
                      ? "Eintrag"
                      : "Einträge"
                  }}
                  berücksichtigt
                </span>
                <span v-else>Keine Einträge vorhanden</span>
                <v-spacer></v-spacer>
                <v-btn
                  text
                  icon
                  small
                  @click.stop
                  @click="toggleDetails(dataVariable)"
                >
                  <v-icon small>
                    {{
                      dataVariable.showDetails
                        ? "mdi-chevron-up"
                        : "mdi-chevron-down"
                    }}
                  </v-icon>
                </v-btn>
              </v-card-actions>

              <!-- Expandable Content -->
              <v-expand-transition>
                <div v-if="dataVariable.showDetails">
                  <v-divider></v-divider>

                  <!-- Loading State -->
                  <v-skeleton-loader
                    v-if="dataIsLoading"
                    class="mx-auto"
                    type="list-item-two-line"
                  ></v-skeleton-loader>

                  <!-- List of Entries -->
                  <v-list
                    v-else-if="dataVariable.additionalData.length"
                    dense
                    class="py-0 rounded"
                  >
                    <div
                      v-for="(entry, entryIndex) in dataVariable.additionalData"
                      :key="entry.id"
                    >
                      <v-list-item>
                        <!-- Duty Attendance Entry -->
                        <v-list-item-content
                          v-if="entry.type === 'dutyAttendance'"
                        >
                          <v-list-item-title class="font-weight-medium mb-2">
                            <span>{{ entry.doc.dutyReport.title }}</span>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-if="entry.doc.connectedTimeSheetId"
                                  icon
                                  x-small
                                  text
                                  class="ml-1"
                                  color="primary"
                                  v-bind="attrs"
                                  v-on="on"
                                  :to="{
                                    name: 'accounting-time-sheets-details',
                                    params: {
                                      organizationId:
                                        $route.params.organizationId,
                                      itemId: entry.doc.connectedTimeSheetId,
                                    },
                                  }"
                                  ><v-icon x-small
                                    >mdi-open-in-app</v-icon
                                  ></v-btn
                                >
                              </template>
                              <span>Stundenmeldung öffnen</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-if="entry.doc.dutyReportId"
                                  icon
                                  x-small
                                  text
                                  color="primary"
                                  class="ml-1"
                                  v-bind="attrs"
                                  v-on="on"
                                  :to="{
                                    name: 'duty-management-reports-details',
                                    params: {
                                      organizationId:
                                        $route.params.organizationId,
                                      itemId: entry.doc.dutyReportId,
                                    },
                                  }"
                                  ><v-icon x-small
                                    >mdi-open-in-app</v-icon
                                  ></v-btn
                                >
                              </template>
                              <span>Dienstbericht öffnen</span>
                            </v-tooltip>
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            <v-row dense class="font-weight-regular">
                              <v-col cols="12" sm="6">
                                <v-icon x-small left>mdi-clock-start</v-icon>
                                {{
                                  timestampConverter(
                                    entry.doc.duration.start.timestamp
                                  )
                                }}
                              </v-col>
                              <v-col cols="12" sm="6">
                                <v-icon x-small left>mdi-clock-end</v-icon>
                                {{
                                  tryToDisplayOnlyEndTime(
                                    entry.doc.duration.start.timestamp,
                                    entry.doc.duration.end.timestamp,
                                    entry.doc.duration.end.timestamp
                                  )
                                }}
                              </v-col>
                              <v-col
                                v-if="
                                  entry.doc.attendanceStatus == '1' &&
                                  entry.doc.payrollType.title
                                "
                                cols="12"
                                sm="6"
                              >
                                <v-icon x-small left
                                  >mdi-account-credit-card</v-icon
                                >
                                {{ entry.doc.payrollType.title }}
                              </v-col>
                              <v-col
                                v-if="
                                  entry.doc.attendanceStatus == '1' &&
                                  entry.doc.assignedPosition
                                "
                                cols="12"
                                sm="6"
                              >
                                <v-icon x-small left>mdi-fire-truck</v-icon>
                                {{ entry.doc.assignedPosition.unit.title }} –
                                {{
                                  entry.doc.assignedPosition.position.shortTitle
                                }}
                              </v-col>
                              <v-col v-if="entry.doc.note" cols="12">
                                <v-icon x-small left>mdi-card-text</v-icon>
                                {{ entry.doc.note }}
                              </v-col>
                            </v-row>
                          </v-list-item-subtitle>
                        </v-list-item-content>

                        <!-- Time Sheet Entry -->
                        <v-list-item-content
                          v-else-if="entry.type === 'timeSheet'"
                        >
                          <v-list-item-title class="font-weight-medium">
                            <v-icon small left color="primary"
                              >mdi-clock-outline</v-icon
                            >
                            {{ entry.doc.title }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider
                        v-if="
                          entryIndex + 1 < dataVariable.additionalData.length
                        "
                        class=""
                      ></v-divider>
                    </div>
                  </v-list>

                  <!-- Empty State -->
                  <v-card-text
                    v-else
                    class="text-center text-caption grey--text py-4"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                    Keine Einträge vorhanden
                  </v-card-text>
                </div>
              </v-expand-transition>
            </div>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from "@/firebase";
export default {
  name: "performance-target-result-calculation-card",
  components: {},
  props: {
    result: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dataIsLoading: true,
      resultDisplay: null,
    };
  },
  computed: {},
  created() {
    this.loadAdditionalData();
  },
  methods: {
    toggleDetails(dataVariable) {
      this.$set(dataVariable, "showDetails", !dataVariable.showDetails);
    },
    async loadAdditionalData() {
      try {
        const updatedItem = { ...this.result };
        const performanceTargetResults = updatedItem.dataVariables;

        for (const variable of performanceTargetResults) {
          // Initialize showDetails property for each variable (make it reactive)
          this.$set(variable, "showDetails", false);

          if (
            variable.additionalData &&
            Array.isArray(variable.additionalData)
          ) {
            for (const additionalDataItem of variable.additionalData) {
              try {
                const basePath = db
                  .collection("organizations")
                  .doc(this.$route.params.organizationId);

                let doc;
                if (additionalDataItem.type === "dutyAttendance") {
                  doc = await basePath
                    .collection("dutyReports")
                    .doc(additionalDataItem.dutyReportId)
                    .collection("dutyAttendances")
                    .doc(additionalDataItem.id)
                    .get();
                } else if (additionalDataItem.type === "timeSheet") {
                  doc = await basePath
                    .collection("timeSheets")
                    .doc(additionalDataItem.id)
                    .get();
                }

                if (doc && doc.exists) {
                  additionalDataItem.doc = { id: doc.id, ...doc.data() };
                }
              } catch (error) {
                console.error(
                  `Error fetching additional data for ${additionalDataItem.type}/${additionalDataItem.id}:`,
                  error
                );
              }
            }
          }
        }

        this.resultDisplay = updatedItem;
      } catch (error) {
        console.error("Error loading additional data:", error);
      } finally {
        this.dataIsLoading = false;
      }
    },
    tryToDisplayOnlyEndTime(startTimestamp, endTimestamp, endTimestampString) {
      var startDate = startTimestamp.toDate();
      var endDate = endTimestamp.toDate();
      if (
        startDate.getDate() == endDate.getDate() &&
        startDate.getMonth() == endDate.getMonth() &&
        startDate.getFullYear() == endDate.getFullYear()
      ) {
        return endDate.toLocaleTimeString("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else {
        return this.timestampConverter(endTimestampString);
      }
    },
    timestampConverter(timestamp) {
      const options = {
        weekday: "short",
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
  },
};
</script>
